import { useCallback, useMemo, useState } from "react";

import { Policyholder } from "@covertree/new-business-graphql";
import {
  selectBasicQuestions,
  selectExtraPolicyDetails,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";
import { formatPhoneNumber, determineIfNoEmailAddress } from "@covertree/utils";

import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  ContactDetailsForm,
  UpdateContactDetailsModal,
} from "./UpdateContactDetailsModal";

interface Props {
  isD2C: boolean;
  onContactDetailsSubmit(data: ContactDetailsForm): Promise<void>;
  policyholder: Policyholder;
}

export function ContactDetails({
  isD2C,
  onContactDetailsSubmit,
  policyholder: {
    emailAddress,
    hasActivePolicies,
    primaryContactNumber,
    secondaryContactNumber,
  },
}: Props) {
  const [contactDetailsModalOpen, setContactDetailsModalOpen] = useState(false);

  const { noEmail, physicallyMailDocuments } = useNewBusinessSelector(
    selectExtraPolicyDetails
  );

  const {
    noEmail: noEmailQuestion,
    physicallyMailDocuments: physicallyMailDocumentsQuestion,
  } = useNewBusinessSelector(selectBasicQuestions);

  const email = useMemo(
    () =>
      noEmail ||
      !emailAddress ||
      determineIfNoEmailAddress(emailAddress, primaryContactNumber)
        ? "None"
        : emailAddress,
    [emailAddress, noEmail, primaryContactNumber]
  );

  const openContactDetailsModal = useCallback(() => {
    setContactDetailsModalOpen(true);
  }, []);

  const closeContactDetailsModal = useCallback(() => {
    setContactDetailsModalOpen(false);
  }, []);

  return (
    <>
      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <Stack direction="column" spacing={1}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Phone number
                </Typography>
                <Typography variant="h5">
                  {formatPhoneNumber(primaryContactNumber)}
                </Typography>
              </Stack>
            </Grid>
            {!isD2C && (
              <Grid item md={5} xs={6}>
                <Stack direction="column" spacing={1}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    Secondary phone number
                  </Typography>
                  <Typography variant="h5">
                    {secondaryContactNumber
                      ? formatPhoneNumber(secondaryContactNumber)
                      : "None"}
                  </Typography>
                </Stack>
              </Grid>
            )}
            <Grid item xs={12} md={isD2C ? 7 : 12}>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: {
                    xs: "flex-end",
                    md: isD2C ? "center" : "flex-end",
                  },
                }}
                justifyContent="space-between">
                <Stack
                  direction="column"
                  spacing={1}
                  sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                  <Typography variant="h6" color="textSecondary">
                    Email
                  </Typography>
                  <Typography
                    variant="h5"
                    noWrap
                    sx={{ textOverflow: "ellipsis" }}>
                    {email}
                  </Typography>
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!!hasActivePolicies}
                  onClick={openContactDetailsModal}>
                  Edit
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <UpdateContactDetailsModal
        isD2C={isD2C}
        emailAddress={emailAddress}
        noEmail={
          noEmail ||
          determineIfNoEmailAddress(emailAddress, primaryContactNumber)
        }
        noEmailQuestion={noEmailQuestion}
        onClose={closeContactDetailsModal}
        onSubmit={onContactDetailsSubmit}
        open={contactDetailsModalOpen}
        physicallyMailDocuments={physicallyMailDocuments ?? undefined}
        physicallyMailDocumentsQuestion={physicallyMailDocumentsQuestion}
        primaryContactNumber={primaryContactNumber}
        secondaryContactNumber={secondaryContactNumber ?? undefined}
      />
    </>
  );
}
